/* ==========================================================================
   WINKELWAGEN: werking (openen/sluiten, aantallen, voortgangsbalk, rollend balkje)
   Instellingen staan in Thema aanpassen > Thema-instellingen > "Winkelwagen: ...".
   Dit bestand hoef je niet aan te passen.
   ========================================================================== */

class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.savingsNode = this.drawer.querySelector('[data-cart-drawer-savings]');
    this.savingsAmountNode = this.drawer.querySelector('[data-cart-drawer-savings-amount]');
    this.showSavings = this.drawer.dataset.cartDrawerSavingsEnabled === 'true';
    this.showDiscountNames = this.drawer.dataset.cartDrawerDiscountNames === 'true';
    this.currency = this.drawer.dataset.cartCurrency || 'EUR';
    this.tipTiers = (this.drawer.dataset.cartDrawerTipTiers || '')
      .split(',')
      .map((part) => part.split(':').map(Number))
      .filter(([quantity, percent]) => quantity > 0 && percent > 0)
      .map(([quantity, percent]) => ({ quantity, percent }));
    this.tipText = this.drawer.dataset.cartDrawerTipText || 'Koop {quantity} stuks → {price} per stuk';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';

    this.setupTicker();
    this.setupProgress();
    this.bindUI();
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  /* ---------- Rollend balkje ---------- */

  setupTicker() {
    const ticker = this.drawer.querySelector('[data-cart-ticker]');
    this.tickerItems = ticker ? [...ticker.querySelectorAll('[data-cart-ticker-item]')] : [];
    this.tickerDelay = Math.max(2, Number(ticker?.dataset.interval) || 4) * 1000;
    this.tickerIndex = 0;
    this.tickerTimer = null;
  }

  showTickerItem(index) {
    this.tickerItems.forEach((node, i) => {
      const active = i === index;
      node.classList.toggle('is-active', active);
      node.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  startTicker() {
    this.stopTicker();
    this.tickerIndex = 0;
    this.showTickerItem(0);
    if (this.tickerItems.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.tickerTimer = window.setInterval(() => {
      this.tickerIndex = (this.tickerIndex + 1) % this.tickerItems.length;
      this.showTickerItem(this.tickerIndex);
    }, this.tickerDelay);
  }

  stopTicker() {
    if (this.tickerTimer) {
      window.clearInterval(this.tickerTimer);
      this.tickerTimer = null;
    }
  }

  /* ---------- Voortgangsbalk ---------- */

  setupProgress() {
    this.progress = this.drawer.querySelector('[data-cart-progress]');
    this.progressMilestones = [];
    if (!this.progress) return;

    this.progressText = this.progress.querySelector('[data-cart-progress-text]');
    this.progressFill = this.progress.querySelector('[data-cart-progress-fill]');
    this.progressMode = this.progress.dataset.mode === 'amount' ? 'amount' : 'items';
    this.progressTextNext = this.progress.dataset.textNext || 'Voeg nog {remaining} toe voor {reward}!';
    this.progressTextDone = this.progress.dataset.textDone || '';
    this.unitOne = this.progress.dataset.unitOne || 'artikel';
    this.unitOther = this.progress.dataset.unitOther || 'artikelen';

    this.progressMilestones = [...this.progress.querySelectorAll('[data-cart-milestone]')].map((node, index, all) => ({
      node,
      threshold: Number(node.dataset.threshold) || 0,
      reward: node.dataset.reward || '',
      position: (index + 1) / all.length
    }));
  }

  updateProgress(cart) {
    if (!this.progress) return;

    const itemCount = cart.item_count || 0;
    this.progress.hidden = itemCount === 0;
    if (itemCount === 0 || !this.progressMilestones.length) return;

    const value =
      this.progressMode === 'amount'
        ? Number(cart.original_total_price ?? cart.total_price ?? 0)
        : itemCount;
    // Bij "aantal artikelen" begint de balk bij het eerste artikel.
    const baseline = this.progressMode === 'items' ? 1 : 0;

    let fill = 0;
    let previousValue = 0;
    let previousPosition = 0;
    let lastReached = null;
    let nextMilestone = null;

    this.progressMilestones.forEach((milestone, index) => {
      const reached = value >= milestone.threshold;
      milestone.node.classList.toggle('is-reached', reached);

      if (reached) {
        lastReached = milestone;
        fill = milestone.position;
        previousValue = index === 0 ? Math.max(milestone.threshold, baseline) : milestone.threshold;
        previousPosition = milestone.position;
      } else if (!nextMilestone) {
        nextMilestone = milestone;
        const span = milestone.threshold - previousValue;
        const share = span > 0 ? Math.min(Math.max((value - previousValue) / span, 0), 1) : 0;
        fill = previousPosition + share * (milestone.position - previousPosition);
      }
    });

    this.progress.style.setProperty('--cv-fill', String(Math.min(Math.max(fill, 0), 1)));

    let html = '';
    if (nextMilestone) {
      const remaining = nextMilestone.threshold - value;
      const remainingLabel =
        this.progressMode === 'amount'
          ? this.formatMoney(remaining)
          : `${remaining} ${remaining === 1 ? this.unitOne : this.unitOther}`;
      html = this.fillProgressText(this.progressTextNext, remainingLabel, nextMilestone.reward);
    } else if (lastReached && this.progressTextDone) {
      html = this.fillProgressText(this.progressTextDone, '', lastReached.reward);
    }

    if (this.progressText) {
      this.progressText.innerHTML = html;
    }
  }

  fillProgressText(template, remaining, reward) {
    return this.escapeHtml(template)
      .replaceAll('{remaining}', `<strong>${this.escapeHtml(remaining)}</strong>`)
      .replaceAll('{reward}', `<strong>${this.escapeHtml(reward)}</strong>`);
  }

  /* ---------- Toevoegen / wijzigen ---------- */

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Unable to add item to cart');
      }

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      window.location.href = form.action || '/cart';
    } finally {
      this.setAddButtonState(false);
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) {
      this.showSkeleton(Math.min(currentCount, 3));
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await response.json();
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = cart.item_count || 0;

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    // "Je bespaart": wat het normaal zou kosten (oude prijs of prijs zonder korting) min wat de klant betaalt
    const referenceTotal = (cart.items || []).reduce((sum, item) => {
      const reference = Math.max(
        item.final_line_price || 0,
        this.showCompareAt ? item.compare_at_line_price || 0 : 0,
        item.original_line_price || 0
      );
      return sum + reference;
    }, 0);
    const savings = referenceTotal - (cart.total_price || 0);
    if (this.savingsNode && this.savingsAmountNode) {
      const showSavingsLine = this.showSavings && savings > 0;
      this.savingsNode.hidden = !showSavingsLine;
      this.savingsAmountNode.textContent = showSavingsLine ? this.formatMoney(savings) : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    this.updateProgress(cart);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="cart-drawer__cta">
            ${this.escapeHtml(this.continueLabel)}
          </a>
        </div>
      `;
      return;
    }

    const trashIcon =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/></svg>';

    const tagIcon =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor"/></svg>';

    const nextTipTier = this.tipTiers
      .filter((tier) => tier.quantity > itemCount)
      .sort((a, b) => a.quantity - b.quantity)[0];

    // Korting op de hele bestelling (niet op een regel) eerlijk verdelen over de regels
    const lineLevelSum = cart.items.reduce(
      (sum, item) => sum + Math.max(0, (item.original_line_price || 0) - (item.final_line_price || 0)),
      0
    );
    const finalSum = cart.items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    const cartLevelExtra =
      finalSum > 0
        ? Math.max(0, Number(cart.original_total_price ?? cart.total_price ?? 0) - (cart.total_price || 0) - lineLevelSum)
        : 0;
    let extraGiven = 0;
    const lineShares = cart.items.map((item, index) => {
      if (!cartLevelExtra) return 0;
      const lineFinal = item.final_line_price || 0;
      let share;
      if (index === cart.items.length - 1) {
        share = cartLevelExtra - extraGiven;
      } else {
        share = Math.floor((cartLevelExtra * lineFinal) / finalSum);
        extraGiven += share;
      }
      return Math.min(Math.max(share, 0), lineFinal);
    });

    const itemsMarkup = cart.items
      .map((item, itemIndex) => {
        const imageSrc = item.image
          ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=240&height=240&crop=center`
          : '';
        const imageMarkup = item.image
          ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
              <img src="${this.escapeHtml(imageSrc)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy" width="240" height="240">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const variantMarkup =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
            : '';

        const lineFinalPrice = (item.final_line_price || 0) - lineShares[itemIndex];
        let lineCompare = this.showCompareAt ? Number(item.compare_at_line_price || 0) : 0;
        if ((item.original_line_price || 0) > lineFinalPrice && item.original_line_price > lineCompare) {
          lineCompare = item.original_line_price;
        }
        const comparePriceMarkup =
          lineCompare > lineFinalPrice
            ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(lineCompare)}</p>`
            : '';

        const discountTitles = this.showDiscountNames
          ? (item.line_level_discount_allocations || []).length
            ? item.line_level_discount_allocations.map((allocation) => allocation.discount_application?.title)
            : (item.discounts || []).map((discount) => discount.title)
          : [];
        const discountMarkup = discountTitles
          .filter(Boolean)
          .map(
            (title) =>
              `<p class="cart-drawer__item-discount">${tagIcon}<span>${this.escapeHtml(title)}</span></p>`
          )
          .join('');

        const isPending = this.pendingItemKeys.has(item.key);

        let tipMarkup = '';
        if (nextTipTier) {
          const unitPrice = Math.round(((item.original_price || 0) * (100 - nextTipTier.percent)) / 100);
          const tipText = this.escapeHtml(this.tipText)
            .replace('{quantity}', () => String(nextTipTier.quantity))
            .replace('{price}', () => `<strong>${this.formatMoney(unitPrice)}</strong>`)
            .replace('{percent}', () => String(nextTipTier.percent));
          tipMarkup = `<p class="cart-drawer__item-tip" data-cart-item-tip>${tipText}</p>`;
        }

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <div class="cart-drawer__item-top">
                <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                <button type="button" class="cart-drawer__remove" data-cart-remove ${isPending ? 'disabled' : ''} aria-label="${this.escapeHtml(this.removeLabel)}: ${this.escapeHtml(item.product_title)}">${trashIcon}</button>
              </div>
              ${variantMarkup}
              ${discountMarkup}
              <div class="cart-drawer__item-bottom">
                <div class="cart-drawer__quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>&minus;</button>
                  <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${isPending ? 'disabled' : ''}>
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
                </div>
                <div class="cart-drawer__item-prices">
                  ${comparePriceMarkup}
                  <p class="cart-drawer__item-price">${this.formatMoney(lineFinalPrice)}</p>
                </div>
              </div>
              ${tipMarkup}
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);
    this.startTicker();

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.stopTicker();
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled') && !node.closest('[hidden]'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});
